/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LoaderFunction, MetaFunction, useLoaderData, useSearchParams } from "react-router";
import LiveDataCard from "~/components/home-screen/LiveDataCard";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import YoutubeVideoPlayer from "~/components/shared-components/YoutubeVideoPlayer";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { LiveData } from "~/interface/home/home";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { removeAudioPlayerKeyFromSession } from "~/lib/helperFuncation/sessionStorageService";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { changeIsAudioPlayState, changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { setCurrentVideo, setCurrentVideoId, setCurrentVideoPosition, setPipMode, setVideoQueue } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { FALLBACK_IMAGE_URL, LANG, LIVE_TV } from "~/utils/constant";
import { homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

interface liveStreamLoaderdata {
  selectedMedia: string;
  selectedType: string;
  liveDataArray: LiveData[];
  selectedTV: LiveData;
  title: string;
}

interface MoreLiveDataCard {
  data: LiveData[];
  selectedTab: string;
  title: string;
  selectedMedia: string;
  handleOnTVClick: (type: string) => void;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const { lang, slug } = params;
  const url = new URL(request.url);
  const selectedLiveTv = slug;

  const apiService = new ApiService(lang || LANG.english);
  const liveDataResponse = await apiService.getLiveData();

  const selectedMedia = selectedLiveTv ?? null;
  const selectedType = selectedLiveTv ? LIVE_TV : null;

  const selectedTvKey = selectedMedia?.replace(/-/g, "_");

  const selectedTV = liveDataResponse.find((item: LiveData) => item.type === selectedTvKey);

  const title = selectedTV?.tv_title || (lang === LANG.english ? "Live" : "લાઇવ");

  return {
    title,
    selectedMedia,
    selectedType,
    liveDataArray: liveDataResponse,
    selectedTV,
  };
};

export const meta: MetaFunction = ({ location, params, data }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const meta = getPageMeta("liveTvRadio", lang);
  const tvdata = data as liveStreamLoaderdata;

  return metaService({
    title: tvdata?.title || meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage ?? FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth ?? "1200",
    ogHeight: meta.ogImageHeight ?? "630",
    canonicalLink: `${pathname}`,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    const slug = match.params.slug;

    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
          ...(slug
            ? [
                {
                  path: `/live`,
                  title: lang === LANG.english ? "Live" : "લાઇવ",
                },
              ]
            : []),
        ]}
      />
    );
  },
};

function liveStream() {
  const { liveDataArray, selectedType, selectedMedia, selectedTV } = useLoaderData<liveStreamLoaderdata>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isVideoPlay } = useAppSelector((state) => state.youtubeVideo);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { navigate } = useLocale();

  const { playCurrentVideo } = useStopCurrentRunningVideoAudio();

  const handleOnTVClick = (type: string) => {
    navigate(`/live/${type.split("_").join("-")}`);
  };

  useEffect(() => {
    const videoQueue = liveDataArray?.map((item) => {
      return {
        id: item?.id,
        videoId: item?.yt_id,
        from: `/live/${item?.type}`,
        name: item?.tv_title || "",
        duration: null,
        thumbnailUrl: getYoutubeVideoThumbnail(item?.yt_id, "medium") || FALLBACK_IMAGE_URL,
      };
    });

    if (selectedTV) {
      const currentVideo = {
        id: selectedTV.id,
        videoId: selectedTV.yt_id,
        from: `/live/${selectedMedia}`,
        name: selectedTV.tv_title || "",
        duration: null,
        thumbnailUrl: getYoutubeVideoThumbnail(selectedTV.yt_id, "medium") || FALLBACK_IMAGE_URL,
      };

      if (videoQueue.length > 0) {
        dispatch(setVideoQueue(videoQueue));
      }

      dispatch(changeIsAudioPlayState(false));
      dispatch(changeIsPlayingState(false));
      removeAudioPlayerKeyFromSession();
      dispatch(setCurrentVideo(currentVideo));

      dispatch(setCurrentVideoId(selectedTV.yt_id));
      playCurrentVideo(selectedTV?.yt_id);
      dispatch(setCurrentVideoPosition(0));
    } else {
      const newParamas = new URLSearchParams();
      newParamas.delete("tv");
      setSearchParams(newParamas, { replace: true });
    }
  }, [selectedTV]);

  useEffect(() => {
    if (!isVideoPlay) {
      dispatch(setPipMode(false));
    }
  }, [isVideoPlay]);

  return (
    <div className="w-full flex flex-col justify-start items-center p-4 gap-8">
      {selectedType === LIVE_TV && selectedTV && (
        <div className="w-full flex flex-col justify-start items-center pb-[16px] gap-3">
          <div className="max-md:w-full md:h-[500px] aspect-[16/9] bg-gray-300 rounded-lg overflow-hidden">
            <YoutubeVideoPlayer
              handlePlayVideo={() => playCurrentVideo(selectedTV?.yt_id)}
              videoId={selectedTV?.yt_id}
              onReady={() => {
                dispatch(setPipMode(true));
              }}
            />
          </div>
        </div>
      )}

      <MoreLiveDataCard
        data={liveDataArray}
        selectedTab={selectedType === LIVE_TV ? LIVE_TV : LIVE_TV}
        title={t("home.liveTvRadio")}
        selectedMedia={selectedMedia}
        handleOnTVClick={handleOnTVClick}
      />
    </div>
  );
}

const MoreLiveDataCard = ({ data, selectedTab, title, selectedMedia, handleOnTVClick }: MoreLiveDataCard) => {
  const { medium } = useFontStyle();
  return (
    <div className="w-full flex flex-col justify-start items-center gap-4 bg-darkBackground rounded-xl p-4 cursor-pointer">
      <div className="w-full flex justify-center md:justify-start px-3 items-center">
        <span className={cn("text-16 text-textColor", medium)}>{title}</span>
      </div>
      <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-5 px-3 w-full", data?.length > 4 ? "md:grid-cols-5" : "md:grid-cols-4")}>
        {data.map((data) => {
          return (
            <React.Fragment key={data.id}>
              <LiveDataCard data={data} selectedTab={selectedTab} selectedMedia={selectedMedia} handleOnTVClick={handleOnTVClick} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default liveStream;
